import { OnboardingStepHeader } from "@/features/onboarding/components/onboarding-step-header";
import { useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import "react-international-phone/style.css";
import Loading from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  useSuspenseQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getEnvVar } from "@/common/utils/environment";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useProviderUser } from "@/hooks/useProviderUser";
import { useTRPC } from "@/trpc";
import { ProviderNpiSelect } from "@/components/Selects/ProviderNpiSelect";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { onboardingAccountValues } from "../utils/onboarding-account-values";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  PencilLine,
  Search,
} from "lucide-react";
import { ProviderDetailsFields } from "@/features/onboarding/components/account-details-fields";
import { ONBOARDING_SELECT_CLASS_NAMES } from "@/features/onboarding/components/onboarding-form-styles";

const formSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    middleName: z.string().optional(),
    lastName: z.string().min(1, "Last name is required"),
    suffixName: z.string().optional(),
    npiNumber: z.string(),
    hasNoNpi: z.boolean(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({
        message: "You must accept the terms of service and privacy policy",
      }),
    }),
    specialtyCodes: z
      .array(z.string())
      .min(1, "At least one specialty is required"),
    healthcareProfessionalType: z
      .string()
      .min(1, "Healthcare role is required"),
    providerNpi: z
      .object({
        firstName: z.string(),
        lastName: z.string(),
        npi: z.string(),
        specialties: z.array(
          z.object({
            code: z.string(),
            desc: z.string().optional().nullable(),
            primary: z.boolean().optional().nullable(),
            state: z.string().optional().nullable(),
            taxonomy_group: z.string().optional().nullable(),
          }),
        ),
        providerTypeId: z.string().nullable(),
        middleName: z.string().optional().nullable(),
        suffixName: z.string().optional().nullable(),
      })
      .nullable(),
    manualEntry: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const npi = data.npiNumber.trim();
    const hasValidNpi = /^\d{10}$/.test(npi);

    if (!data.hasNoNpi && !hasValidNpi) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["npiNumber"],
        message: `Enter a 10-digit NPI or select "I don't have an NPI".`,
      });
    }
  });

export type AccountDetailsFormValues = z.infer<typeof formSchema>;

const NPI_SEARCH_INPUT_ID = "onboarding-npi-search";

const PRIVACY_POLICY_URL =
  getEnvVar("PRIVACY_POLICY_URL") || "https://www.saileapp.com/privacy-policy";
const TERMS_OF_SERVICE_URL =
  getEnvVar("TERMS_OF_SERVICE_URL") ||
  "https://www.saileapp.com/terms-of-service";

const openUrl = async (url: string) => {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.open(url, "_blank");
  }
};

export default function AccountDetailsOnboardingForm({
  onComplete,
}: {
  onComplete: () => void;
}) {
  const trpc = useTRPC();
  const providerUser = useProviderUser();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: healthcareRoles } = useSuspenseQuery(
    trpc.healthcareProfessionalTypes.getAll.queryOptions(undefined, {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    }),
  );

  const credentialsQuery = useSuspenseQuery(
    trpc.providerOnboarding.getSavedDetails.queryOptions(),
  );
  const saved = onboardingAccountValues({
    saved: credentialsQuery.data.account,
    healthcareRoles,
  });
  const healthcareRolesData = healthcareRoles.map((role) => ({
    value: role.id,
    label: role.name,
  }));
  const hasSavedData = saved.hasSavedData;

  const [entryMethod, setEntryMethod] = useState<"search" | "manual">(
    hasSavedData ? "manual" : "search",
  );

  const form = useForm<AccountDetailsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: saved.firstName,
      middleName: saved.middleName,
      lastName: saved.lastName,
      suffixName: saved.suffixName,
      npiNumber: saved.npiNumber,
      hasNoNpi: saved.hasNoNpi,
      healthcareProfessionalType: saved.healthcareProfessionalType,
      specialtyCodes: saved.specialtyCodes,
      providerNpi: null,
      manualEntry: hasSavedData,
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;
  const providerNpi = watch("providerNpi");
  const hasNoNpi = watch("hasNoNpi");

  const { mutateAsync: updateAccountDetails } = useMutation({
    ...trpc.providerOnboarding.updateDetails.mutationOptions(),
    onSuccess: async () => {
      setSubmitError(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.providerOnboarding.getSavedDetails.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.credentials.getMyCredentials.queryKey({
            switchUserId: providerUser.id,
          }),
        }),
        queryClient.invalidateQueries(
          trpc.provider.getProviderUser.queryOptions(),
        ),
      ]);
      onComplete();
    },
    onError: () => {
      setSubmitError("An error occurred. Please try again.");
    },
  });

  const onSubmit = async (data: AccountDetailsFormValues) => {
    try {
      setSubmitError(null);
      const payload = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        suffixName: data.suffixName,
        npiNumber: data.hasNoNpi ? null : data.npiNumber.trim(),
        healthcareProfessionalTypeId: data.healthcareProfessionalType,
        specialtyCodes: data.specialtyCodes,
      };
      await updateAccountDetails(payload);
    } catch (error) {
      setSubmitError("An error occurred while saving. Please try again.");
    }
  };

  useEffect(() => {
    if (providerNpi) {
      setValue("firstName", providerNpi.firstName, { shouldValidate: false });
      setValue("middleName", providerNpi.middleName ?? "", {
        shouldValidate: false,
      });
      setValue("lastName", providerNpi.lastName, { shouldValidate: false });
      setValue("suffixName", providerNpi.suffixName ?? "", {
        shouldValidate: false,
      });
      setValue("npiNumber", providerNpi.npi, { shouldValidate: false });

      const specialtyCodes = providerNpi.specialties
        .map((s) => s.code)
        .filter((code) => code !== null);
      setValue("specialtyCodes", specialtyCodes, { shouldValidate: false });
      if (providerNpi.providerTypeId) {
        setValue("healthcareProfessionalType", providerNpi.providerTypeId, {
          shouldValidate: false,
        });
      } else {
        setValue("healthcareProfessionalType", "", { shouldValidate: false });
      }
      setValue("hasNoNpi", false, { shouldValidate: false });
    }
  }, [providerNpi, setValue]);

  // Clear NPI number when "No NPI?" is checked
  useEffect(() => {
    if (hasNoNpi) {
      setValue("npiNumber", "", { shouldValidate: false });
    }
  }, [hasNoNpi, setValue]);

  useEffect(() => {
    setValue("manualEntry", entryMethod === "manual");
  }, [entryMethod, setValue]);

  // Check if there are any form errors that should be displayed
  const hasFormErrors = Object.keys(errors).length > 0;
  const generalErrorMessage = hasFormErrors
    ? "Please check the form for errors and try again."
    : submitError;

  return (
    <div>
      <OnboardingStepHeader
        title="Account Details"
        description="Start with your NPI record or enter your information below. You can review everything before saving."
      />
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {!providerNpi && (
            <Tabs
              defaultValue="search"
              value={entryMethod}
              onValueChange={(value) =>
                setEntryMethod(value as "search" | "manual")
              }
            >
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1">
                <TabsTrigger
                  value="search"
                  className="min-h-12 gap-2 rounded-lg px-2 text-sm text-slate-500 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                >
                  <Search
                    className="hidden size-4 shrink-0 sm:block"
                    aria-hidden="true"
                  />
                  Find my NPI
                </TabsTrigger>
                <TabsTrigger
                  value="manual"
                  className="min-h-12 gap-2 rounded-lg px-2 text-sm text-slate-500 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                >
                  <PencilLine
                    className="hidden size-4 shrink-0 sm:block"
                    aria-hidden="true"
                  />
                  Enter manually
                </TabsTrigger>
              </TabsList>

              <TabsContent value="search" className="mt-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6">
                  <FormField
                    name="providerNpi"
                    control={control}
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel
                          htmlFor={NPI_SEARCH_INPUT_ID}
                          className="block text-base font-semibold text-slate-900"
                        >
                          Find your NPI record
                        </FormLabel>
                        <FormDescription className="text-sm leading-6 text-slate-500">
                          Search by full name or 10-digit NPI. We&apos;ll fill
                          in the details for you to review.
                        </FormDescription>
                        <FormControl>
                          <ProviderNpiSelect
                            inputId={NPI_SEARCH_INPUT_ID}
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            placeholder="Enter your full name or NPI number"
                            appearance="profile"
                            classNames={ONBOARDING_SELECT_CLASS_NAMES}
                            size="lg"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="mt-5 flex flex-wrap items-center gap-x-3 border-t border-slate-200 pt-3">
                    <Button
                      variant="link"
                      size="none"
                      className="min-h-11 p-0 text-sm font-semibold underline-offset-4"
                      type="button"
                      onClick={() => {
                        setEntryMethod("manual");
                        setValue("hasNoNpi", true);
                      }}
                    >
                      Don&apos;t have an NPI?
                    </Button>
                    <p className="text-xs leading-5 text-slate-500">
                      You can enter your details manually.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="manual" className="mt-5">
                <ProviderDetailsFields
                  control={control}
                  healthcareRolesData={healthcareRolesData}
                  hasNoNpi={hasNoNpi}
                />
              </TabsContent>
            </Tabs>
          )}

          {providerNpi && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-primary/5 px-4 py-3">
                <div className="flex items-center gap-2.5 text-sm font-medium text-primary">
                  <CheckCircle2
                    className="size-5 shrink-0"
                    aria-hidden="true"
                  />
                  NPI record selected. Review your details below.
                </div>
                <Button
                  variant="link"
                  size="none"
                  type="button"
                  className="min-h-11 gap-1.5 p-0 text-sm underline-offset-4"
                  onClick={() => setValue("providerNpi", null)}
                >
                  <ArrowLeft className="size-4" aria-hidden="true" />
                  Change NPI
                </Button>
              </div>
              <ProviderDetailsFields
                control={control}
                healthcareRolesData={healthcareRolesData}
                hasNoNpi={hasNoNpi}
              />
            </div>
          )}

          <FormField
            name="acceptTerms"
            control={control}
            render={({ field }) => (
              <FormItem className="flex items-start gap-3 space-y-0 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-1 shrink-0"
                  />
                </FormControl>
                <div className="space-y-2">
                  <FormLabel className="block cursor-pointer text-sm font-medium leading-6 text-slate-700">
                    Agree to our{" "}
                    <button
                      type="button"
                      onClick={() => openUrl(TERMS_OF_SERVICE_URL)}
                      className="text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
                    >
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      onClick={() => openUrl(PRIVACY_POLICY_URL)}
                      className="text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
                    >
                      Privacy Policy
                    </button>
                    .
                  </FormLabel>
                  <p className="text-xs leading-5 text-slate-500">
                    Please review both policies before continuing.
                  </p>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          {generalErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
            >
              <CircleAlert
                className="mt-0.5 size-5 shrink-0 text-red-600"
                aria-hidden="true"
              />
              <p className="text-sm leading-6 text-red-700">
                {generalErrorMessage}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-4 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500">
              Fields marked with <span className="text-destructive">*</span> are
              required.
            </p>
            <Button
              size="lg"
              className="w-full gap-2 rounded-xl px-7 sm:w-auto sm:min-w-32"
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loading className="size-4" />
                  Saving…
                </>
              ) : (
                <>
                  Save
                  <ArrowRight className="size-4" aria-hidden="true" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
